/**
 * Algorithm Detail Page
 * Tabs: Overview | Pseudocode | Dry Run | Problems
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { algorithmService } from '../services/api';
import DryRunViewer from '../components/algorithm/DryRunViewer';
import { useBookmarks, useRecentlyViewed } from '../hooks';
import './AlgorithmDetailPage.css';

const TABS = [
  { id: 'overview', label: '📋 Overview' },
  { id: 'pseudocode', label: '💻 Pseudocode' },
  { id: 'dryrun', label: '▶ Dry Run' },
];

function AlgorithmDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [algorithm, setAlgorithm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const { isBookmarked, toggle } = useBookmarks();
  const { addRecent } = useRecentlyViewed();

  useEffect(() => {
    setLoading(true);
    algorithmService.getById(id)
      .then(res => {
        setAlgorithm(res.data);
        addRecent({ id: res.data.id, name: res.data.name, type: 'algorithm' });
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  if (loading) return <div className="loader" style={{ height: '50vh' }}><div className="spinner" /></div>;
  if (error) return (
    <div className="container" style={{ padding: '60px 24px', textAlign: 'center' }}>
      <p>⚠️ {error}</p>
      <button className="btn btn-secondary" onClick={() => navigate('/algorithms')} style={{ marginTop: 16 }}>
        Back to Algorithms
      </button>
    </div>
  );
  if (!algorithm) return null;

  const bookmarked = isBookmarked(algorithm.id);

  return (
    <div className="detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/algorithms">Algorithms</Link>
          <span>›</span>
          <span>{algorithm.name}</span>
        </div>

        {/* Hero */}
        <div className="detail-hero">
          <div className="detail-hero-left">
            <div className="detail-badges">
              <span className={`badge badge-${algorithm.difficulty?.toLowerCase()}`}>{algorithm.difficulty}</span>
              <span className="badge badge-accent">{algorithm.unit}</span>
              <span className="badge badge-accent">{algorithm.category}</span>
            </div>
            <h1>{algorithm.fullName}</h1>
            <p className="detail-desc">{algorithm.longDescription}</p>

            {/* Tags */}
            <div className="detail-tags">
              {algorithm.tags?.map(tag => (
                <span key={tag} className="tag">#{tag}</span>
              ))}
            </div>
          </div>

          <div className="detail-hero-right">
            {/* Complexity Card */}
            <div className="complexity-card card">
              <h4>Complexity Analysis</h4>
              <div className="complexity-grid">
                <div className="c-row">
                  <span>Time (Best)</span>
                  <span className="complexity-badge">{algorithm.timeComplexity?.best}</span>
                </div>
                <div className="c-row">
                  <span>Time (Avg)</span>
                  <span className="complexity-badge">{algorithm.timeComplexity?.average}</span>
                </div>
                <div className="c-row">
                  <span>Time (Worst)</span>
                  <span className="complexity-badge">{algorithm.timeComplexity?.worst}</span>
                </div>
                <div className="c-row">
                  <span>Space</span>
                  <span className="complexity-badge">{algorithm.spaceComplexity}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="detail-actions">
              <button
                className={`btn ${bookmarked ? 'btn-secondary' : 'btn-ghost'}`}
                onClick={() => toggle(algorithm.id)}
              >
                {bookmarked ? '★ Bookmarked' : '☆ Bookmark'}
              </button>
              <Link
                to={`/problems?algorithmId=${algorithm.id}`}
                className="btn btn-primary"
              >
                Solve Problems →
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-tab">
              {/* Steps */}
              <div className="detail-section">
                <h3>How It Works</h3>
                <ol className="steps-list">
                  {algorithm.steps?.map((step, i) => (
                    <li key={i} className="step-list-item">
                      <span className="step-list-num">{i + 1}</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="detail-two-col">
                {/* Advantages */}
                <div className="detail-section card">
                  <h3 className="text-green">✓ Advantages</h3>
                  <ul className="pro-con-list">
                    {algorithm.advantages?.map((a, i) => (
                      <li key={i}><span className="text-green">+</span> {a}</li>
                    ))}
                  </ul>
                </div>

                {/* Disadvantages */}
                <div className="detail-section card">
                  <h3 className="text-red">✗ Disadvantages</h3>
                  <ul className="pro-con-list">
                    {algorithm.disadvantages?.map((d, i) => (
                      <li key={i}><span className="text-red">−</span> {d}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Pseudocode Tab */}
          {activeTab === 'pseudocode' && (
            <div className="pseudocode-tab">
              <div className="pseudocode-header">
                <h3>Pseudocode</h3>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigator.clipboard?.writeText(algorithm.pseudocode)}
                >
                  📋 Copy
                </button>
              </div>
              <pre className="pseudocode-block">{algorithm.pseudocode}</pre>
            </div>
          )}

          {/* Dry Run Tab */}
          {activeTab === 'dryrun' && (
            <div className="dryrun-tab">
              <div className="dryrun-info card">
                <p>
                  🎬 This is an animated step-by-step execution of <strong>{algorithm.name}</strong> on sample input.
                  Press Play or use the step controls to walk through the algorithm.
                </p>
              </div>
              <DryRunViewer dryRun={algorithm.dryRun} algorithmName={algorithm.name} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AlgorithmDetailPage;
